import { useState } from 'react';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';

/**
 * Kleine Bausteinsammlung statt einer Komponentenbibliothek.
 *
 * Begruendung: das Dashboard hat ueberschaubar viele Elemente. shadcn/ui wuerde
 * Radix und einen Generator mitbringen, ohne dass wir dessen Umfang brauchen.
 * Was hier steht, erfuellt dieselben Anforderungen an Fokus, Tastatur und
 * Touch-Größe, bleibt aber lesbar.
 */

function cx(...values: Array<string | false | undefined | null>): string {
  return values.filter(Boolean).join(' ');
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'md' | 'sm';
  loading?: boolean;
};

export function Button({ variant = 'primary', size = 'md', loading, className, children, disabled, ...rest }: ButtonProps) {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-[background-color,border-color,transform] duration-200 ease-[var(--ease-out-soft)] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer';
  const sizes = size === 'sm' ? 'min-h-10 px-3.5 text-sm' : 'min-h-11 px-5 text-[15px]';
  const variants = {
    primary: 'bg-brand-600 text-white hover:bg-brand-700',
    secondary: 'bg-[var(--surface)] text-[var(--text)] border border-[var(--border)] hover:border-brand-400',
    ghost: 'text-[var(--text-muted)] hover:text-[var(--text)] hover:bg-brand-50/60',
    danger: 'bg-danger-500 text-white hover:brightness-110',
  }[variant];

  return (
    <button className={cx(base, sizes, variants, className)} disabled={disabled || loading} {...rest}>
      {loading && <Spinner />}
      {children}
    </button>
  );
}

export function Spinner() {
  return (
    <svg className="size-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cx(
        'rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-5 sm:p-6',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function SectionTitle({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
        {hint && <p className="mt-1 text-sm text-[var(--text-muted)]">{hint}</p>}
      </div>
      {action}
    </div>
  );
}

type FieldProps = InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string; error?: string };

export function Field({ label, hint, error, id, className, ...rest }: FieldProps) {
  const inputId = id ?? `f-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-sm font-medium">
        {label}
      </label>
      <input
        id={inputId}
        className={cx(
          'min-h-11 w-full rounded-xl border bg-[var(--surface)] px-3.5 text-[15px] outline-none transition-colors',
          error ? 'border-danger-500' : 'border-[var(--border)] focus:border-brand-500',
          className,
        )}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? inputId + '-error' : hint ? inputId + '-hint' : undefined}
        {...rest}
      />
      {hint && !error && (
        <p id={inputId + '-hint'} className="text-xs text-[var(--text-muted)]">
          {hint}
        </p>
      )}
      {error && (
        <p id={inputId + '-error'} role="alert" className="text-xs font-medium text-danger-500">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Passwortfeld mit Anzeigen-Schalter.
 *
 * Ein Passwort blind eintippen zu müssen, ist die häufigste Ursache für
 * gescheiterte Anmeldungen, besonders bei langen Zeichenketten aus dem
 * Passwortmanager. Der Schalter ist ein Button, kein Icon ohne Beschriftung,
 * und mit 44px gross genug für den Daumen.
 */
export function PasswordField({ label, hint, error, id, ...rest }: Omit<FieldProps, 'type'>) {
  const [sichtbar, setSichtbar] = useState(false);
  const inputId = id ?? `p-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-sm font-medium">
        {label}
      </label>
      <div className="relative">
        <input
          id={inputId}
          type={sichtbar ? 'text' : 'password'}
          className={cx(
            'min-h-11 w-full rounded-xl border bg-[var(--surface)] py-2 pl-3.5 pr-24 text-[15px] outline-none transition-colors',
            error ? 'border-danger-500' : 'border-[var(--border)] focus:border-brand-500',
          )}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? inputId + '-error' : hint ? inputId + '-hint' : undefined}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setSichtbar((offen) => !offen)}
          aria-pressed={sichtbar}
          aria-controls={inputId}
          className="absolute right-1 top-1/2 flex min-h-9 -translate-y-1/2 cursor-pointer items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold text-[var(--text-muted)] transition-colors hover:text-[var(--text)]"
        >
          <EyeIcon offen={sichtbar} />
          {sichtbar ? 'Verbergen' : 'Anzeigen'}
        </button>
      </div>
      {hint && !error && (
        <p id={inputId + '-hint'} className="text-xs text-[var(--text-muted)]">
          {hint}
        </p>
      )}
      {error && (
        <p id={inputId + '-error'} role="alert" className="text-xs font-medium text-danger-500">
          {error}
        </p>
      )}
    </div>
  );
}

function EyeIcon({ offen }: { offen: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      {offen ? (
        <>
          <path d="M3 3l18 18" strokeLinecap="round" />
          <path d="M10.6 5.2A9.7 9.7 0 0 1 12 5c5 0 9 4.5 9 7 0 .9-.5 2-1.4 3.1M6.5 6.9C4.4 8.3 3 10.3 3 12c0 2.5 4 7 9 7 1.7 0 3.2-.5 4.5-1.3" strokeLinecap="round" />
          <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" strokeLinecap="round" />
        </>
      ) : (
        <>
          <path d="M3 12s3.5-7 9-7 9 7 9 7-3.5 7-9 7-9-7-9-7Z" />
          <circle cx="12" cy="12" r="3" />
        </>
      )}
    </svg>
  );
}

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & { label: string };

export function Select({ label, id, children, className, ...rest }: SelectProps) {
  const selectId = id ?? `s-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={selectId} className="text-sm font-medium">
        {label}
      </label>
      <select
        id={selectId}
        className={cx(
          'min-h-11 w-full cursor-pointer rounded-xl border border-[var(--border)] bg-[var(--surface)] px-3 text-[15px] outline-none focus:border-brand-500',
          className,
        )}
        {...rest}
      >
        {children}
      </select>
    </div>
  );
}

const CATEGORY_STYLES: Record<string, string> = {
  necessary: 'bg-ok-100 text-ok-500',
  preferences: 'bg-brand-100 text-brand-700',
  statistics: 'bg-[#dbeafe] text-accent-600',
  marketing: 'bg-warn-100 text-warn-500',
  unclassified: 'bg-danger-100 text-danger-500',
};

export function CategoryBadge({ category, label }: { category: string; label?: string }) {
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold',
        CATEGORY_STYLES[category] ?? 'bg-ink-100 text-ink-600',
      )}
    >
      {label ?? category}
    </span>
  );
}

export function StatTile({
  label,
  value,
  sub,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'neutral' | 'good' | 'warn';
}) {
  const accent = tone === 'good' ? 'text-ok-500' : tone === 'warn' ? 'text-warn-500' : 'text-[var(--text)]';
  return (
    <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-muted)]">{label}</p>
      <p className={cx('tabular mt-1.5 text-2xl font-semibold', accent)}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-[var(--text-muted)]">{sub}</p>}
    </div>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-4 py-2">
      <span>
        <span className="block text-sm font-medium">{label}</span>
        {description && <span className="mt-0.5 block text-xs text-[var(--text-muted)]">{description}</span>}
      </span>
      <span className="relative inline-flex shrink-0">
        <input
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          className="peer absolute inset-0 size-full cursor-pointer opacity-0"
        />
        <span className="block h-7 w-12 rounded-full bg-ink-400 transition-colors duration-200 peer-checked:bg-brand-600 peer-focus-visible:outline peer-focus-visible:outline-3 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-600" />
        <span className="pointer-events-none absolute left-0.5 top-0.5 size-6 rounded-full bg-white shadow transition-transform duration-200 peer-checked:translate-x-5" />
      </span>
    </label>
  );
}

export function EmptyState({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-dashed border-[var(--border)] p-8 text-center">
      <p className="font-semibold">{title}</p>
      <p className="mx-auto mt-1.5 max-w-md text-sm text-[var(--text-muted)]">{body}</p>
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function Alert({ tone, children }: { tone: 'info' | 'warn' | 'error'; children: ReactNode }) {
  const styles = {
    info: 'bg-brand-50 text-brand-900 border-brand-200',
    warn: 'bg-warn-100 text-[#7c2d12] border-warn-500/40',
    error: 'bg-danger-100 text-[#7f1d1d] border-danger-500/40',
  }[tone];
  return (
    <div role={tone === 'error' ? 'alert' : undefined} className={cx('rounded-xl border px-4 py-3 text-sm', styles)}>
      {children}
    </div>
  );
}

export { cx };
